import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';

const attendeesCollection = collection(db, "attendees");
const deletedAttendeesCollection = collection(db, "deleted_attendees");
const doctorsMasterDataCollection = collection(db, "doctors_master_data");
const eventsCollection = collection(db, "events");

const attendeeService = {

  getAll: async () => {
    try {
      const attendeesSnapshot = await getDocs(attendeesCollection);
      const attendeesList = attendeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return attendeesList;
    } catch (error) {
      console.error('Error fetching attendees:', error);
      throw error;
    }
  },
  add: async (attendeesData) => {
    try {
      const docRef = await addDoc(attendeesCollection, attendeesData);
      return docRef;
    } catch (error) {
      console.error('Error adding attendees:', error);
      throw error;
    }
  },
  edit: async ({ id, data }) => {
    try {
      const attendeesRef = doc(db, 'attendees', id);
      await updateDoc(attendeesRef, data);
    } catch (error) {
      console.error('Error updating attendees:', error);
      throw error;
    }
  },


  delete: async (attendeeId) => {
    try {
      // Get the attendee data before deletion
      const attendeeRef = doc(db, "attendees", attendeeId);
      const attendeeSnap = await getDoc(attendeeRef);

      if (attendeeSnap.exists()) {
        // Store in deleted_attendees collection with original data and deletion timestamp
        await addDoc(deletedAttendeesCollection, {
          ...attendeeSnap.data(),
          original_id: attendeeId,
          deleted_at: new Date().toISOString()
        });

        // Delete from main collection
        await deleteDoc(attendeeRef);
        return true;
      }

      throw new Error('Attendee not found');
    } catch (error) {
      console.error('Error deleting attendee:', error);
      throw error;
    }
  },

  getEventAttendees: async (eventId) => {
    try {
      // ✅ OPTIMIZED: Query only attendees for this specific event
      const eventAttendeesQuery = query(
        attendeesCollection,
        where('event_id', '==', eventId)
      );
      
      const attendeesSnapshot = await getDocs(eventAttendeesQuery);
      const eventAttendees = attendeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return eventAttendees;
    }
    catch (error) {
      console.error('Error fetching attendees:', error);
      throw error;
    }
  },

  checkAttendeeDuplicateEmailNationalIdAPONumber: async ({ eventId, email, national_id, apo_number }) => {
    try {
      const queries = [];

      // Check for duplicate email
      if (email) {
        queries.push(
          getDocs(query(
            attendeesCollection,
            where('event_id', '==', eventId),
            where('email', '==', email.toLowerCase())
          ))
        );
      }

      // Check for duplicate national ID
      if (national_id) {
        queries.push(
          getDocs(query(
            attendeesCollection,
            where('event_id', '==', eventId),
            where('national_id', '==', national_id)
          ))
        );
      }

      // Check for duplicate APO number
      if (apo_number) {
        queries.push(
          getDocs(query(
            attendeesCollection,
            where('event_id', '==', eventId),
            where('APO_number', '==', apo_number)
          ))
        );
      }

      // Execute all queries in parallel
      const results = await Promise.all(queries);

      const duplicateEmail = email ? results[0]?.docs.length > 0 : false;
      const duplicateNationalId = national_id ? results[email ? 1 : 0]?.docs.length > 0 : false;
      const duplicateApoNumber = apo_number ? results[queries.length - 1]?.docs.length > 0 : false;

      return { duplicateEmail, duplicateNationalId, duplicateApoNumber };
    }
    catch (error) {
      console.error('Error checking duplicates:', error);
      throw error;
    }
  },

  getAttendeesInEventWithKeyDoctorsMasterData: async (eventId) => {
    try {
      // Get the specific event document
      const eventDoc = await getDoc(doc(eventsCollection, eventId));

      if (!eventDoc.exists()) {
        throw new Error(`Event with ID ${eventId} not found.`);
      }

      const event = {
        id: eventDoc.id,
        ...eventDoc.data()
      };

      // Get attendees linked to the event
      const attendeesSnapshot = await getDocs(
        query(attendeesCollection, where('event_id', '==', event.id))
      );

      const attendees = await Promise.all(attendeesSnapshot.docs.map(async attendeeDoc => {
        const attendeeData = { id: attendeeDoc.id, ...attendeeDoc.data() };
        let isUpdated = false;

        if (attendeeData.doctor_master_data_id) {
          const doctorRef = doc(doctorsMasterDataCollection, attendeeData.doctor_master_data_id);
          const doctorSnap = await getDoc(doctorRef);

          if (doctorSnap.exists()) {
            const doctorData = { id: doctorSnap.id, ...doctorSnap.data() };

            const fieldsToCompare = [
              'first_name', 'family_name', 'email', 'phone_number', 'specialty', 'medical_license',
              'APO_number', 'national_id', 'country_id',
            ];

            const hasChanges = fieldsToCompare.some(field =>
              attendeeData[field] !== doctorData[field]
            );

            if (hasChanges) {
              const updateData = fieldsToCompare.reduce((acc, field) => {
                acc[field] = doctorData[field];
                return acc;
              }, {});

              await updateDoc(attendeeDoc.ref, updateData);
              isUpdated = true;

              return {
                ...attendeeData,
                ...updateData,
                isUpdated
              };
            }
          }
        }

        return {
          ...attendeeData,
          isUpdated
        };
      }));

      return {
        eventId: event.id,
        attendees,
        hasUpdates: attendees.some(attendee => attendee.isUpdated)
      };

    } catch (error) {
      console.error('Error fetching attendees with doctors data:', error);
      throw error;
    }
  }

};

export default attendeeService;

